import { z } from 'zod';

/**
 * Tickets and bug reports share one table. A bug report is a ticket whose `type`
 * is `bug` and which carries extra reproduction fields — keeping them together
 * means SLA timers, comments, attachments and assignment work identically for
 * both, and a support ticket can be converted to a bug without losing history.
 */

export const TICKET_TYPES = ['incident', 'request', 'bug', 'change', 'question'] as const;
export type TicketType = (typeof TICKET_TYPES)[number];

export const TICKET_STATES = ['new', 'open', 'pending', 'on_hold', 'resolved', 'closed'] as const;
export type TicketState = (typeof TICKET_STATES)[number];

export const TICKET_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type TicketPriority = (typeof TICKET_PRIORITIES)[number];

/** Response/resolution targets in minutes, by priority. */
export const SLA_TARGETS: Record<TicketPriority, { respondMin: number; resolveMin: number }> = {
  urgent: { respondMin: 15, resolveMin: 240 },
  high: { respondMin: 60, resolveMin: 480 },
  normal: { respondMin: 240, resolveMin: 2880 },
  low: { respondMin: 1440, resolveMin: 10080 },
};

export const ticketSchema = z.object({
  id: z.string(),
  /** Human reference shown to customers, e.g. "TIK-1042". */
  reference: z.string(),
  type: z.enum(TICKET_TYPES),
  subject: z.string().min(1),
  body: z.string().default(''),
  state: z.enum(TICKET_STATES).default('new'),
  priority: z.enum(TICKET_PRIORITIES).default('normal'),
  customerId: z.string().nullable().default(null),
  /** Resource the ticket is about, when known. */
  resourceId: z.string().nullable().default(null),
  /** Alert that auto-opened this ticket. */
  alertId: z.string().nullable().default(null),
  reporterId: z.string().nullable().default(null),
  reporterEmail: z.string().nullable().default(null),
  assigneeId: z.string().nullable().default(null),
  tags: z.array(z.string()).default([]),

  // bug-report specific — ignored for other types
  severity: z.enum(['cosmetic', 'minor', 'major', 'blocker']).nullable().default(null),
  component: z.string().nullable().default(null),
  version: z.string().nullable().default(null),
  stepsToReproduce: z.string().nullable().default(null),
  expectedBehaviour: z.string().nullable().default(null),
  actualBehaviour: z.string().nullable().default(null),
  /** Deduplicates identical crash/bug submissions from agents. */
  fingerprint: z.string().nullable().default(null),
  duplicateOfId: z.string().nullable().default(null),
  occurrences: z.number().int().default(1),

  firstResponseAt: z.number().nullable().default(null),
  resolvedAt: z.number().nullable().default(null),
  closedAt: z.number().nullable().default(null),
  dueAt: z.number().nullable().default(null),
  slaBreached: z.boolean().default(false),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Ticket = z.infer<typeof ticketSchema>;

export const ticketCreateSchema = ticketSchema
  .omit({
    id: true,
    reference: true,
    createdAt: true,
    updatedAt: true,
    firstResponseAt: true,
    resolvedAt: true,
    closedAt: true,
    dueAt: true,
    slaBreached: true,
    occurrences: true,
    duplicateOfId: true,
  })
  .partial()
  .required({ subject: true, type: true });
export type TicketCreate = z.infer<typeof ticketCreateSchema>;

export const ticketCommentSchema = z.object({
  id: z.string(),
  ticketId: z.string(),
  authorId: z.string().nullable(),
  authorName: z.string(),
  body: z.string(),
  /** Internal notes are hidden from `customer` role users. */
  internal: z.boolean().default(false),
  attachments: z
    .array(z.object({ name: z.string(), url: z.string(), sizeBytes: z.number(), mime: z.string() }))
    .default([]),
  createdAt: z.number(),
});
export type TicketComment = z.infer<typeof ticketCommentSchema>;
